/* THE TRAINING CALENDAR'S LAYOUT — pure, so `calendar.test.ts` executes it.
 *
 * `EVERFIT-MAP.md` §2b-bis measured theirs: Monday-first, a tall cell per day, `1 Week | 2 Week |
 * 4 Week`, an `Assignment ⇄ History` pair, `Save as Program`, and a hover-only `+` in each cell.
 *
 * 🔴 TWO THINGS WE DO NOT COPY, AND BOTH ARE MEASUREMENTS RATHER THAN TASTE.
 *   * Their `+` appears on HOVER and is absent otherwise — §12 executed
 *     `main a[href],main button,main [tabindex]` on this screen and got ZERO. A coach on a
 *     keyboard cannot reach the one control the screen exists for.
 *     🔴 **AMENDED 2026-09-19 BY W22(g) (Fable's ruling), AND THE SENTENCE IT REPLACED WAS TRUE
 *     OF THE MARKUP AND FALSE OF THE SCREEN.** This file said ours is *"a real button, always in
 *     the DOM … the cheapest falsifiable 'better than Everfit' on the product"* — while `onAddTo`
 *     had NO CALLER anywhere in `src/`, so the button was never drawn once and the claim was
 *     unfalsifiable rather than falsifiable. It is wired now, and the LOOK is theirs: hidden at
 *     rest, revealed by the day cell's hover. What stays ours is the semantics — a real
 *     `<button>` in the tab order, revealed by its own focus, always drawn where there is no
 *     hover at all.
 *     ⚠️ A claim about a control is a claim about a CALL SITE. This one lived in three files and
 *     none of them could see that nobody passed the prop.
 *   * Their `4 Week` ceiling is a PAYWALL — *"Upgrade to schedule more than 4 weeks ahead"* sits
 *     under the grid. We take the same three spans because they suit a coach's eye, and nothing
 *     here stops a fourth.
 *
 * ⚠️ AND THE MODEL DOES NOT MOVE: the PROGRAM is the truth and the calendar RENDERS it (his
 * 2026-08-27 sentence, and §7's rule). Nothing in this file decides what a week contains.
 */
import { dayKeyOf, shiftDayKey } from "./days";
import { mondayKey } from "./weekLine";

export type Span = 1 | 2 | 4;
export const SPANS: Span[] = [1, 2, 4];

export type CalendarItem = {
  id: string;
  name: string;
  /** A task is shown but never counted as training — `roster.ts`'s one rule. */
  isTask: boolean;
  /** An OPTIONAL task (`isOptionalTask`): drawn with the word, never with the trained tick. */
  optional?: boolean;
  trained: boolean;
  /** W11b — the `coach_assignments` row, when this item IS one. Absent on a task, which is why
   *  the day card draws no `⋯` for tasks: none of the three actions applies to them. */
  assignmentId?: string;
  /**
   * W22(f) — the `coach_workouts` id this assignment points at, for the EXPANDED cell's tiles.
   *
   * ⚠️ **ABSENT WHEN THE PAGE DOES NOT HOLD ONE, NEVER `""`.** The Client page carries an
   * `assignmentId → workoutId` map built from the same read the list uses, and a row it has no
   * entry for is a row whose tree cannot be fetched — an empty string would be an id that is not
   * one, which is how a read ends up asking the door about nothing. Absence is spelled as
   * absence, exactly as `assignmentId` above spells a task's.
   */
  workoutId?: string;
  /** W137 (R7) — the order on its day (`day_position`) and the tie-break (`assigned_at`); absent = 0 / null. */
  dayPosition?: number;
  assignedAtISO?: string | null;
};

export type CalendarDay = {
  dayKey: string;
  dayOfMonth: number;
  isToday: boolean;
  /** Before today, so the cell may say something happened — or did not. */
  isPast: boolean;
  items: CalendarItem[];
};

export type CalendarWeek = { mondayKey: string; days: CalendarDay[] };

/** Monday-first, always — never the locale's week start (`ProgramWeekCalendar`'s rule, web side). */
export function calendarWeeks(
  today: Date,
  span: Span,
  weekOffset: number,
  itemsByDay: Map<string, CalendarItem[]>,
): CalendarWeek[] {
  const out: CalendarWeek[] = [];
  const todayKey = dayKeyOf(today);
  for (let w = 0; w < span; w += 1) {
    const monday = mondayKey(today, weekOffset + w);
    const days: CalendarDay[] = [];
    for (let d = 0; d < 7; d += 1) {
      const dayKey = shiftDayKey(new Date(`${monday}T00:00:00Z`), d);
      days.push({
        dayKey,
        dayOfMonth: Number(dayKey.slice(8, 10)),
        isToday: dayKey === todayKey,
        // ⚠️ STRICTLY BEFORE. Today is not past — an assignment due today has not been missed,
        // which is the same boundary `outcome` draws and the reason his banner said 5 when it
        // meant 1.
        isPast: dayKey < todayKey,
        items: itemsByDay.get(dayKey) ?? [],
      });
    }
    out.push({ mondayKey: monday, days });
  }
  return out;
}

/**
 * The range a coach reads in the toolbar — `Sep 14 – Sep 27`.
 *
 * ⚠️ IT IS DERIVED FROM THE WEEKS, NEVER FROM THE SPAN. A label computed as "monday + span×7 − 1"
 * would keep saying the right thing only while the two agree; deriving it from the first and last
 * day actually rendered means the words and the grid cannot disagree.
 */
export function rangeLabel(weeks: CalendarWeek[], say: (dayKey: string) => string): string {
  const first = weeks[0]?.days[0];
  const last = weeks[weeks.length - 1]?.days[6];
  if (first === undefined || last === undefined) return "";
  return `${say(first.dayKey)} – ${say(last.dayKey)}`;
}

/* ── W20(c): THE SPAN LIVES IN THE URL — theirs, and copyable today ───────────────────────────
 *
 * `docs/EVERFIT-MAP.md` §2-ter depth 3, on their TASKS tab: *"🔴 The view is in the URL:
 * `?calendarWeekView=2`"*. Their key and their values, verbatim — a coach pasting a link to a
 * colleague should land on the same view, and copying the spelling costs nothing while
 * inventing our own would make the two products disagree about a thing they agree on.
 *
 * ⚠️ AND IT IS READ THROUGH `useQuerySort`, WHICH LISTENS TO `hashchange`. The 2026-09-18 bug is
 * this file's nearest neighbour: a value read in a `useState` INITIALISER is read ONCE, and these
 * screens do not remount on navigation — so a pasted link parsed correctly and changed nothing.
 * A span read that way would look exactly as correct and be exactly as dead.
 */
export const SPAN_QUERY_KEY = "calendarWeekView";

/**
 * The span a URL asks for, or the default.
 *
 * ⚠️ TWO IS THE DEFAULT BECAUSE IT IS THEIRS — `2 Week` is the selected pill on his own account
 * (§2b-bis and §2-ter both). Anything unrecognised — a missing key, `"3"`, `"banana"`, `"-1"` —
 * resolves to it rather than to the nearest number: a URL that asks for a span we do not have is
 * a URL that asks for nothing, and guessing "they probably meant 4" is how a pasted link shows
 * somebody a different screen from the one that was sent.
 */
export function spanFromQuery(q: Record<string, string>, stored: string | null = null): Span {
  /* W140 (S4): a URL with no span falls back to the coach's REMEMBERED span (`viewPrefs` "weekSpan"), then to 2. The URL
     still wins when it says one — a pasted link opens as sent — and an unrecognised stored word is ignored like a URL's. */
  const ok = (raw: string | null | undefined): Span | null => {
    if (raw == null || raw === "") return null;
    const n = Number(raw);
    return (SPANS as number[]).includes(n) ? (n as Span) : null;
  };
  return ok(q[SPAN_QUERY_KEY]) ?? ok(stored) ?? 2;
}

/** What to write back. Always spelled the same way, so one view is always one URL. */
export function spanQuery(span: Span): string {
  return `?${SPAN_QUERY_KEY}=${String(span)}`;
}

/* ⚠️ W22(c)'s `+ N more` CAP (`DAY_ITEM_CAP`, `daySplit`, `moreLabel`) WAS REMOVED 2026-09-25 (W72 step 2):
   the copied lifted panel it served is replaced by square cells that open on their own (`dayCells.ts`). */

/* ── W71 — his 2026-09-24 night asks on the Training calendar (NEXT-WORK W71, Fable's rulings) ──
 *
 * *"shouldn't we mention month in all days? Like 21/9 and so on? Not just 21. Plus the main workout
 * should show first, and the tasks only as +Tasks or something like that."* */

/** `21/9` — the day and the month as he wrote them, for EVERY day cell on both calendars. The one
 *  formatter; a second spelling at a call site is how two calendars come to disagree. */
export function dayMonthLabel(dayKey: string): string {
  return `${String(Number(dayKey.slice(8, 10)))}/${String(Number(dayKey.slice(5, 7)))}`;
}

/** The Training cell's order: the day's WORKOUTS, then its tasks folded into one line. */
export function workoutsThenTasks<T extends { isTask: boolean }>(items: readonly T[]): { workouts: T[]; tasks: T[] } {
  return { workouts: items.filter((i) => !i.isTask), tasks: items.filter((i) => i.isTask) };
}

/** `+ 1 task` / `+ 3 tasks` — his "+Tasks", with the count. */
export function tasksFoldLabel(n: number): string {
  return `+ ${String(n)} ${n === 1 ? "task" : "tasks"}`;
}


/* ── W22(d): THE DATE LIVES IN THE URL, BESIDE THE VIEW ───────────────────────────────────────
 *
 * `EVERFIT-MAP` §2b-ter depth 3, read live: their calendar carries TWO query parameters, not one
 * — `?tab=assignment&date=2026-09-07`. So a coach can send a colleague a link to a SPECIFIC WEEK
 * rather than to "whatever two weeks you happen to be standing in". W20(c) copied their
 * `calendarWeekView` for the Tasks tab; `date` was copied by nobody.
 *
 * 🔴 A DATE AND NOT AN OFFSET, AND THAT IS THE WHOLE POINT. `offset` is what this component holds
 * in state, and a link carrying `offset=-2` means a different fortnight tomorrow than it did
 * today — the one thing a shared link must never do. The date is absolute; the offset is DERIVED
 * from it against the reader's own today.
 *
 * ⚠️ AND AN UNPARSEABLE DATE IS `null`, NEVER A GUESS. `?date=banana`, `?date=2026-13-40` and a
 * missing key all mean "this link asks for no particular week", which lands the coach on today —
 * the same rule `spanFromQuery` takes for a span it does not have.
 */
export const DATE_QUERY_KEY = "date";

/** The day a URL asks for, or null. Validated by SHAPE and by the calendar — `2026-02-30` is not
 *  a day, and `new Date` would silently roll it forward to March. */
export function dateFromQuery(q: Record<string, string>): string | null {
  const raw = q[DATE_QUERY_KEY];
  if (typeof raw !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null;
  const d = new Date(`${raw}T00:00:00Z`);
  return Number.isNaN(d.getTime()) || dayKeyOf(d) !== raw ? null : raw;
}

/** Whole weeks from the reader's own Monday to the Monday of `dayKey`.
 *
 *  ⚠️ `Math.round` AND `Math.floor` ARE EQUIVALENT HERE, AND THAT IS RECORDED RATHER THAN
 *  DEFENDED. Both operands are `Date.parse` of a day KEY — `mondayKey` returns a `YYYY-MM-DD`
 *  string, so the time of day is already gone — which makes the quotient an exact integer and a
 *  mutation arm swapping the two SURVIVE. `round` is kept as the honest spelling of "this is a
 *  whole number of weeks"; what actually holds the property is that both sides are keys, and the
 *  arm that measures the DAY instead of its Monday IS caught. */
export function offsetForDate(today: Date, dayKey: string): number {
  const here = Date.parse(`${mondayKey(today, 0)}T00:00:00Z`);
  const there = Date.parse(`${mondayKey(new Date(`${dayKey}T00:00:00Z`), 0)}T00:00:00Z`);
  return Math.round((there - here) / (7 * 86_400_000));
}

/** Both parameters, always spelled the same way, so one view is always one URL. */
export function calendarQuery(span: Span, firstDayKey: string): string {
  return `?${SPAN_QUERY_KEY}=${String(span)}&${DATE_QUERY_KEY}=${firstDayKey}`;
}

/** What the URL is asking for, as one value — the two are read together or they can disagree. */
export type CalendarView = { span: Span; date: string | null };

/* 🔴 THE CALENDAR WRITES ITS OWN KEYS AND KEEPS EVERY OTHER ONE (2026-09-24, his live report:
 * *"tap plus in a day and sometimes it will bring me in the overview tab"*). `calendarQuery` and
 * `spanQuery` build a query from NOTHING — `?calendarWeekView=…&date=…` — and both calendars wrote
 * that over the address with `replaceState`, which drops `tab=training`. Nothing broke at that
 * moment (no `hashchange`, the tab lived on in React state), so it read as fine; the NEXT hash
 * write — the day's `+`, which rebuilds the address from `routeQuery(window.location.hash)` — found
 * no tab and the page fell to Overview with the picker open. "Sometimes" = only after a step, a
 * span change or Today. ONE rule now: a writer starts from the address as it is and sets only its
 * own keys. The bare builders stay for readers that construct a link from scratch. */
export function calendarQueryKeeping(q: Record<string, string>, span: Span, firstDayKey: string): string {
  const next = new URLSearchParams(q);
  next.set(SPAN_QUERY_KEY, String(span));
  next.set(DATE_QUERY_KEY, firstDayKey);
  return `?${next.toString()}`;
}
export function spanQueryKeeping(q: Record<string, string>, span: Span): string {
  const next = new URLSearchParams(q);
  next.set(SPAN_QUERY_KEY, String(span));
  return `?${next.toString()}`;
}

export function viewFromQuery(q: Record<string, string>, storedSpan: string | null = null): CalendarView {
  return { span: spanFromQuery(q, storedSpan), date: dateFromQuery(q) };
}
